import clsx from "clsx";
import { ChangeEvent, FC, KeyboardEvent, useCallback } from "react";

import type { ScalarValue } from "@tsmono/inspect-common/query";
import { AutocompleteInput } from "@tsmono/react/components";

import styles from "./ColumnFilterEditor.module.css";
import { DurationInput } from "./DurationInput";
import { OPERATOR_LABELS } from "./operators";
import { isUiOperator } from "./types";
import type { FilterType, UiOperator } from "./types";

interface FilterValueInputProps {
  id: string;
  filterType: FilterType;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  autoFocus?: boolean;
  suggestions?: ScalarValue[];
  onCommit?: () => void;
  onCancel?: () => void;
  /** Accessible name, when no visible <label> is associated with the control
   *  (range inputs get their name from the visible Start/End labels instead). */
  ariaLabel?: string;
}

/** Renders the type-appropriate value control, shared by both conditions'
 *  value and range-end inputs. */
const FilterValueInput: FC<FilterValueInputProps> = ({
  id,
  filterType,
  value,
  onChange,
  disabled,
  autoFocus = false,
  suggestions = [],
  onCommit,
  onCancel,
  ariaLabel,
}) => {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  if (filterType === "boolean") {
    return (
      <select
        id={id}
        className={styles.filterSelect}
        aria-label={ariaLabel}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- see the note on ConditionRow's autoFocus
        autoFocus={autoFocus}
      >
        <option value="">(not set)</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  if (filterType === "string" || filterType === "unknown") {
    return (
      <AutocompleteInput
        id={id}
        value={value}
        onChange={onChange}
        onCommit={onCommit}
        onCancel={onCancel}
        disabled={disabled}
        placeholder="Filter"
        ariaLabel={ariaLabel}
        suggestions={suggestions}
        className={styles.filterInput}
        autoFocus={autoFocus}
        allowBrowse={true}
      />
    );
  }

  if (filterType === "duration") {
    return (
      <DurationInput
        id={id}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        autoFocus={autoFocus}
        ariaLabel={ariaLabel}
      />
    );
  }

  return (
    <input
      id={id}
      className={styles.filterInput}
      aria-label={ariaLabel}
      type={
        filterType === "number"
          ? "number"
          : filterType === "date"
            ? "date"
            : "datetime-local"
      }
      spellCheck="false"
      value={value}
      onChange={handleChange}
      placeholder="Filter"
      disabled={disabled}
      step={filterType === "number" ? "any" : undefined}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- see the note on ConditionRow's autoFocus
      autoFocus={autoFocus}
    />
  );
};

/** Editing state + handlers for one condition of a (possibly paired) filter. */
export interface ConditionEditorProps {
  operator: UiOperator;
  onOperatorChange: (operator: UiOperator) => void;
  value: string;
  onValueChange: (value: string) => void;
  value2: string;
  onValue2Change: (value: string) => void;
  isValueDisabled: boolean;
  isRangeOperator: boolean;
}

interface ConditionRowProps {
  columnId: string;
  filterType: FilterType;
  operatorOptions: UiOperator[];
  condition: ConditionEditorProps;
  /**
   * "" for condition 1, "-b" for condition 2. The `-b` suffix exists because
   * `-val2` is already taken by condition 1's range end (condition 2 uses
   * `-op-b` / `-val-b` / `-val-b2`).
   */
  idSuffix: "" | "-b";
  /**
   * The filter editor only ever renders inside a popover the user just
   * opened, where moving focus to the first field is the expected behaviour
   * (WAI-ARIA APG dialog pattern) rather than a page-load focus steal.
   */
  autoFocus?: boolean;
  suggestions: ScalarValue[];
  onCommit?: () => void;
  onCancel?: () => void;
}

/** One condition's rows: operator select + value input(s). */
const ConditionRow: FC<ConditionRowProps> = ({
  columnId,
  filterType,
  operatorOptions,
  condition,
  idSuffix,
  autoFocus = false,
  suggestions,
  onCommit,
  onCancel,
}) => {
  const {
    operator,
    onOperatorChange,
    value,
    onValueChange,
    value2,
    onValue2Change,
    isValueDisabled,
    isRangeOperator,
  } = condition;

  const handleOperatorChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      if (isUiOperator(value)) onOperatorChange(value);
    },
    [onOperatorChange]
  );

  return (
    <>
      <div className={styles.filterRow}>
        <select
          id={`${columnId}-op${idSuffix}`}
          className={styles.filterSelect}
          aria-label="Filter operator"
          value={operator}
          onChange={handleOperatorChange}
        >
          {operatorOptions.map((option) => (
            <option key={option} value={option}>
              {OPERATOR_LABELS[option]}
            </option>
          ))}
        </select>
      </div>
      {isRangeOperator && (
        <label
          className={styles.rangeLabel}
          htmlFor={`${columnId}-val${idSuffix}`}
        >
          Start
        </label>
      )}
      <div className={styles.filterRow}>
        <FilterValueInput
          id={`${columnId}-val${idSuffix}`}
          filterType={filterType}
          value={value}
          onChange={onValueChange}
          disabled={isValueDisabled}
          autoFocus={autoFocus}
          suggestions={suggestions}
          onCommit={onCommit}
          onCancel={onCancel}
          // Range inputs are named by the visible Start/End <label>s; the
          // single-value case has no visible label, so name it directly.
          ariaLabel={isRangeOperator ? undefined : "Filter value"}
        />
      </div>
      {isRangeOperator && (
        <>
          <label
            className={styles.rangeLabel}
            htmlFor={`${columnId}-val${idSuffix}2`}
          >
            End
          </label>
          <div className={styles.filterRow}>
            <FilterValueInput
              id={`${columnId}-val${idSuffix}2`}
              filterType={filterType}
              value={value2}
              onChange={onValue2Change}
              disabled={isValueDisabled}
              suggestions={suggestions}
              onCommit={onCommit}
              onCancel={onCancel}
            />
          </div>
        </>
      )}
    </>
  );
};

export interface ColumnFilterEditorProps {
  columnId: string;
  filterType: FilterType;
  operatorOptions: UiOperator[];
  condition: ConditionEditorProps;
  /** Second condition of an AND/OR pair; pass only while it should render
   *  (i.e. once condition 1 has content). */
  second?: ConditionEditorProps;
  join?: "and" | "or";
  onJoinChange?: (join: "and" | "or") => void;
  onCommit?: () => void;
  onCancel?: () => void;
  suggestions?: ScalarValue[];
}

export const ColumnFilterEditor: FC<ColumnFilterEditorProps> = ({
  columnId,
  filterType,
  operatorOptions,
  condition,
  second,
  join = "and",
  onJoinChange,
  onCommit,
  onCancel,
  suggestions = [],
}) => {
  const handleJoinChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === "and" || value === "or") onJoinChange?.(value);
    },
    [onJoinChange]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Prevent parent (grid) bubbling
      event.stopPropagation();
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel?.();
      }
      if (event.key === "Enter") {
        event.preventDefault();
        onCommit?.();
      }
    },
    [onCancel, onCommit]
  );

  return (
    // Escape/Enter are delegated from the fields inside, all of which are
    // focusable in their own right — the container is not itself a control.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={styles.filterContent} onKeyDown={handleKeyDown}>
      <ConditionRow
        columnId={columnId}
        filterType={filterType}
        operatorOptions={operatorOptions}
        condition={condition}
        idSuffix=""
        autoFocus
        suggestions={suggestions}
        onCommit={onCommit}
        onCancel={onCancel}
      />
      {second && (
        <>
          <div
            className={clsx(styles.filterRow, styles.joinRow)}
            role="radiogroup"
            aria-label="Join conditions"
          >
            <label className={styles.joinOption}>
              <input
                type="radio"
                name={`${columnId}-join`}
                value="and"
                checked={join === "and"}
                onChange={handleJoinChange}
              />
              AND
            </label>
            <label className={styles.joinOption}>
              <input
                type="radio"
                name={`${columnId}-join`}
                value="or"
                checked={join === "or"}
                onChange={handleJoinChange}
              />
              OR
            </label>
          </div>
          <ConditionRow
            columnId={columnId}
            filterType={filterType}
            operatorOptions={operatorOptions}
            condition={second}
            idSuffix="-b"
            suggestions={suggestions}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        </>
      )}

      <div className={styles.filterRow}>
        <button
          type="button"
          className={clsx(
            "btn",
            "btn-outline-primary",
            styles.filterButton,
            "text-size-small"
          )}
          onClick={onCommit}
        >
          Apply
        </button>
      </div>
    </div>
  );
};
